import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Check, ShoppingBag, ArrowUpRight, AlertCircle, ChevronLeft, ChevronRight, ZoomIn, X } from 'lucide-react';
import { SiteHeader } from './SiteHeader';
import { SiteFooter } from './SiteFooter';
import { ProductTile } from './ProductTile';
import { InquiryDialog } from './InquiryDialog';
import { ZoomableImage } from './ZoomableImage';
import { WallPreview } from './WallPreview';
import { useCart } from '@/context/CartContext';
import {
  getCollection,
  requiresSize,
  APPAREL_SIZES,
  LEGACY_SLUG_MAP,
} from '@/data/catalog';
import { useProducts } from '@/context/ProductsContext';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useJsonLd } from '@/hooks/useJsonLd';
import { cn } from '@/lib/utils';

export const ProductPage = () => {
  const { slug } = useParams();
  const { getProductBySlug, getProductsByCollection, loading } = useProducts();
  const product = getProductBySlug(slug);
  const { addItem, openCart } = useCart();
  const [imgIndex, setImgIndex] = useState(0);
  const [justAdded, setJustAdded] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [sizeError, setSizeError] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [dedication, setDedication] = useState('');
  const [zoomOpen, setZoomOpen] = useState(false);
  const [pickedCardIds, setPickedCardIds] = useState([]);
  const [selectedPrintSize, setSelectedPrintSize] = useState(null);
  const [wallOpen, setWallOpen] = useState(false);
  const [customSizeOpen, setCustomSizeOpen] = useState(false);

  // When the product changes (or first loads), pick a sensible default size.
  useEffect(() => {
    if (!product) return;
    if (Array.isArray(product.sizes) && product.sizes.length) {
      const match = product.sizes.find((s) => s.price === product.price)
        || product.sizes[product.sizes.length - 1];
      setSelectedPrintSize(match.label);
    } else {
      setSelectedPrintSize(null);
    }
  }, [product]);

  // ESC closes the zoom lightbox
  useEffect(() => {
    if (!zoomOpen) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') setZoomOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [zoomOpen]);

  usePageMeta({
    title: product
      ? `${product.title} — Graves Collective`
      : 'Product — Graves Collective',
    description: product?.description,
    image: product?.image,
  });

  useJsonLd(
    product
      ? {
          '@context': 'https://schema.org/',
          '@type': 'Product',
          name: product.title,
          image: [product.image, ...(product.images?.map((i) => i.url) || [])],
          description: product.description,
          brand: { '@type': 'Brand', name: 'Graves Collective' },
          sku: product.id,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            price: product.price ?? undefined,
            availability:
              product.price === null || product.price === undefined
                ? 'https://schema.org/InStock'
                : 'https://schema.org/InStock',
            url: typeof window !== 'undefined' ? window.location.href : undefined,
          },
        }
      : null,
    'ld-json-product'
  );

  if (!product) {
    // Legacy slug? Redirect to the new URL permanently.
    if (LEGACY_SLUG_MAP[slug]) {
      return <Navigate to={`/products/${LEGACY_SLUG_MAP[slug]}`} replace />;
    }
    // If we're still loading the API, hold the page rather than redirecting
    if (loading) {
      return (
        <div className="min-h-screen bg-cream text-ink flex items-center justify-center" data-testid="product-loading">
          <p className="text-sm uppercase tracking-[0.18em] text-soft">Loading...</p>
        </div>
      );
    }
    return <Navigate to="/404" replace />;
  }

  const hasMultipleImages = product.images && product.images.length > 1;
  const currentImage = hasMultipleImages
    ? product.images[imgIndex].url
    : product.image;
  const collection = getCollection(product.collection);
  const related = getProductsByCollection(product.collection)
    .filter((p) => p.slug !== product.slug)
    .slice(0, 3);

  const needsSize = requiresSize(product);
  const isBook = product.collection === 'books';
  const isCardSampler = product.id === 'pg-008';
  const isSingleBook = product.id === 'cb-001' || product.id === 'cb-002';
  const isSold = !!product.sold;
  const isPrint =
    product.originalCategory === 'prints' ||
    product.originalCategory === 'limited-edition-prints';
  const isOriginalPainting = product.collection === 'originals';
  const canPreviewOnWall = (isPrint || isOriginalPainting) && !isSold;

  // Print size ladder (auto-attached to open-edition prints by enrichProduct).
  // Selected size drives the displayed price and the cart line's unit price.
  const printSizes = Array.isArray(product.sizes) && product.sizes.length ? product.sizes : null;
  const activePrintSize = printSizes
    ? printSizes.find((s) => s.label === selectedPrintSize) || printSizes[0]
    : null;
  const effectivePrice = activePrintSize ? activePrintSize.price : product.price;

  const displayPrice =
    product.price !== null
      ? printSizes && effectivePrice !== null ? `$${effectivePrice}` : `$${product.price}`
      : product.priceLabel || 'Inquire';

  // Card Sampler picker cards
  const allCards = getProductsByCollection('studio')
    .filter((p) => p.originalCategory === 'paper-goods' && p.id !== 'pg-008' && p.price === 10);
  const toggleCard = (id) => {
    setPickedCardIds((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 3) return cur; // cap at 3
      return [...cur, id];
    });
  };

  const bookBundle = isSingleBook
    ? getProductsByCollection('books').find((p) => p.id === 'cb-003')
    : null;

  const handleAddToCart = () => {
    if (isSold) return;
    if (product.price === null) {
      setInquiryOpen(true);
      return;
    }
    if (needsSize && !selectedSize) {
      setSizeError(true);
      return;
    }
    if (isCardSampler && pickedCardIds.length !== 3) {
      return; // guarded by disabled button; belt + suspenders
    }
    const cardsNote = isCardSampler
      ? `Cards: ${pickedCardIds
          .map((id) => allCards.find((c) => c.id === id)?.title.replace(' Greeting Card', ''))
          .filter(Boolean)
          .join(', ')}`
      : null;
    // Print size wins for the "size" field & unit price; apparel size otherwise.
    const sizeForCart = activePrintSize ? activePrintSize.label : selectedSize;
    addItem(
      product,
      sizeForCart,
      currentImage,
      isBook ? dedication.trim() || null : null,
      cardsNote,
      activePrintSize ? activePrintSize.price : null
    );
    setJustAdded(true);
    setSizeError(false);
    setDedication('');
    setPickedCardIds([]);
    setTimeout(() => setJustAdded(false), 1800);
    // gentle open cart after a beat
    setTimeout(() => openCart(), 600);
  };

  return (
    <div
      className="min-h-screen bg-cream text-ink"
      data-testid={`page-product-${slug}`}
    >
      <SiteHeader />

      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 lg:pt-20 pb-24 lg:pb-32">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-soft mb-10">
          <Link
            to={`/${product.collection}`}
            className="inline-flex items-center gap-2 hover:text-terracotta transition-colors group"
            data-testid="breadcrumb-collection"
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-1" />
            {collection?.name || 'Shop'}
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Image column */}
          <div className="lg:col-span-7">
            <BookAwareImage
              product={product}
              imgIndex={imgIndex}
              setImgIndex={setImgIndex}
              hasMultipleImages={hasMultipleImages}
              currentImage={currentImage}
              onZoom={() => setZoomOpen(true)}
            />

            {/* Apparel keeps the tab UI (Front / Back / Lifestyle) */}
            {hasMultipleImages && product.collection !== 'books' && (
              <div
                className="mt-6 flex gap-3 flex-wrap"
                data-testid="product-image-toggle"
              >
                {product.images.map((img, idx) => (
                  <button
                    key={img.id}
                    onClick={() => setImgIndex(idx)}
                    className={cn(
                      'px-5 py-2.5 text-xs uppercase tracking-[0.18em] border transition-all',
                      imgIndex === idx
                        ? 'bg-ink text-cream border-ink'
                        : 'bg-cream text-ink border-ink/20 hover:border-ink'
                    )}
                    data-testid={`image-view-${img.id}`}
                  >
                    {img.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info column */}
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-6">
              {collection?.name}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              {product.title}
            </h1>

            <div className="mt-10 pb-10 border-b border-ink/10">
              <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-3">
                Price
              </p>
              <p
                className={cn(
                  'font-display text-4xl tabular-nums',
                  product.price === null && 'italic text-terracotta'
                )}
                data-testid="product-price"
              >
                {displayPrice}
              </p>
            </div>

            <p className="mt-10 text-base lg:text-lg text-soft leading-[1.7]">
              {product.description}
            </p>

            {/* Print size selector — auto-shown when a product carries a sizes[] array */}
            {printSizes && (
              <div className="mt-10" data-testid="print-size-selector">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-soft">Print size</p>
                  {activePrintSize && (
                    <p className="text-xs text-ink" data-testid="selected-print-size">
                      Selected · <span className="font-medium">{activePrintSize.label}</span>
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {printSizes.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => setSelectedPrintSize(s.label)}
                      className={cn(
                        'py-2.5 px-4 text-xs uppercase tracking-[0.18em] border transition-all flex items-baseline gap-2',
                        selectedPrintSize === s.label
                          ? 'bg-ink text-cream border-ink'
                          : 'bg-cream text-ink border-ink/20 hover:border-ink'
                      )}
                      data-testid={`print-size-${s.label.replace(/[^a-z0-9]/gi, '').toLowerCase()}`}
                    >
                      <span>{s.label}</span>
                      <span className="text-[10px] tabular-nums opacity-70">${s.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Prints & Originals — "See on the wall" preview and print-only extras */}
            {(canPreviewOnWall || product.printUrl) && (
              <div
                className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-soft"
                data-testid="print-extras"
              >
                {canPreviewOnWall && (
                  <button
                    type="button"
                    onClick={() => setWallOpen(true)}
                    className="inline-flex items-center gap-1.5 uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors"
                    data-testid="wall-preview-open"
                  >
                    See on the wall
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                )}
                {product.printUrl && (
                  <a
                    href={product.printUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors"
                    data-testid="original-print-link"
                  >
                    {isOriginalPainting
                      ? (isSold ? 'Available as a signed print at Level Press' : 'Also as a signed print at Level Press')
                      : product.originalCategory === 'limited-edition-prints'
                        ? 'Framed edition at Level Press'
                        : 'Also at Level Press'}
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                )}
                {isPrint && (
                  <>
                    <button
                      type="button"
                      onClick={() => setCustomSizeOpen(true)}
                      className="inline-flex items-center gap-1.5 uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors"
                      data-testid="custom-size-open"
                    >
                      Need 16×20 or larger?
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                    <span className="leading-relaxed w-full sm:w-auto">
                      <span className="uppercase tracking-[0.14em] text-ink">Ships from Denver</span>
                      {' — '}signed and packed by hand · 3–5 business days.
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Size selector — only for sized apparel */}
            {needsSize && (
              <div className="mt-10" data-testid="size-selector">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-soft">Size</p>
                  {selectedSize && (
                    <p className="text-xs text-ink" data-testid="selected-size-label">
                      Selected · <span className="font-medium">{selectedSize}</span>
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {APPAREL_SIZES.map((size) => (
                    <button
                      key={size}
                      onClick={() => {
                        setSelectedSize(size);
                        setSizeError(false);
                      }}
                      className={cn(
                        'min-w-[52px] py-2.5 px-4 text-xs uppercase tracking-[0.18em] border transition-all',
                        selectedSize === size
                          ? 'bg-ink text-cream border-ink'
                          : 'bg-cream text-ink border-ink/20 hover:border-ink'
                      )}
                      data-testid={`size-option-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                {sizeError && (
                  <p
                    className="mt-3 inline-flex items-center gap-2 text-xs text-terracotta"
                    data-testid="size-error"
                  >
                    <AlertCircle className="w-3.5 h-3.5" />
                    Please choose a size before adding to cart.
                  </p>
                )}
                <p className="mt-4 text-xs text-soft">
                  Standard unisex sizing. Need a size guide?{' '}
                  <Link to="/contact" className="text-ink hover:text-terracotta underline">
                    Email the studio
                  </Link>
                  .
                </p>
              </div>
            )}

            {/* Personal dedication — books only */}
            {isBook && (
              <div className="mt-10" data-testid="book-dedication">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-soft">
                    Signed for someone special?
                  </p>
                  <p className="text-[11px] text-soft tabular-nums">{dedication.length}/120</p>
                </div>
                <textarea
                  value={dedication}
                  onChange={(e) => setDedication(e.target.value.slice(0, 120))}
                  placeholder="Optional — write a name or short note and Joseph will hand-inscribe your copy."
                  rows={3}
                  className="w-full px-4 py-3 text-sm bg-transparent border border-ink/20 focus:outline-none focus:border-ink placeholder:text-soft/70 text-ink resize-y"
                  data-testid="book-dedication-input"
                />
                <p className="mt-2 text-xs text-soft leading-relaxed">
                  Hand-signed by the artist at no extra cost. Please allow 2–3 extra days for
                  personalized copies.
                </p>
              </div>
            )}

            {/* Card Sampler picker — Pick 3 for $25 */}
            {isCardSampler && (
              <div className="mt-10" data-testid="card-sampler-picker">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-soft">
                    Choose your 3 cards
                  </p>
                  <p className="text-xs text-ink tabular-nums">
                    <span className={cn(pickedCardIds.length === 3 && 'text-terracotta font-medium')}>
                      {pickedCardIds.length}
                    </span>
                    &nbsp;/ 3 selected
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {allCards.map((card) => {
                    const picked = pickedCardIds.includes(card.id);
                    const disabled = !picked && pickedCardIds.length >= 3;
                    return (
                      <button
                        key={card.id}
                        type="button"
                        onClick={() => toggleCard(card.id)}
                        disabled={disabled}
                        className={cn(
                          'relative text-left transition-all border',
                          picked
                            ? 'border-terracotta ring-2 ring-terracotta/30'
                            : 'border-ink/15 hover:border-ink',
                          disabled && 'opacity-40 cursor-not-allowed'
                        )}
                        data-testid={`card-pick-${card.id}`}
                        aria-pressed={picked}
                      >
                        <div className="aspect-[4/5] bg-ink/[0.04] overflow-hidden">
                          <img
                            src={card.image}
                            alt={card.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {picked && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-terracotta text-cream rounded-full flex items-center justify-center text-xs font-medium tabular-nums">
                            {pickedCardIds.indexOf(card.id) + 1}
                          </div>
                        )}
                        <p className="px-2 py-2 text-[11px] uppercase tracking-[0.08em] text-ink line-clamp-2 leading-tight">
                          {card.title.replace(' Greeting Card', '')}
                        </p>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-soft leading-relaxed">
                  Mix and match any three — Joseph packs each set by hand with matching envelopes.
                </p>
              </div>
            )}

            <div className="mt-12 flex flex-col gap-3">
              {isSold ? (
                <>
                  <div
                    className="inline-flex items-center justify-center gap-3 px-8 py-5 text-sm uppercase tracking-[0.24em] font-medium bg-ink/10 text-ink cursor-default border border-ink/30"
                    data-testid="sold-badge"
                  >
                    Sold — in a private collection
                  </div>
                  <button
                    onClick={() => setInquiryOpen(true)}
                    className="mt-1 text-xs uppercase tracking-[0.18em] text-terracotta hover:underline"
                    data-testid="sold-commission-btn"
                  >
                    Inquire about a similar commission →
                  </button>
                </>
              ) : product.price !== null ? (
                <button
                  onClick={handleAddToCart}
                  disabled={isCardSampler && pickedCardIds.length !== 3}
                  className={cn(
                    'inline-flex items-center justify-center gap-3 px-8 py-5',
                    'text-sm uppercase tracking-[0.18em] font-medium',
                    'bg-ink text-cream hover:bg-terracotta transition-colors',
                    justAdded && 'bg-terracotta',
                    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-ink'
                  )}
                  data-testid="add-to-cart-button"
                >
                  {justAdded ? (
                    <>
                      <Check className="w-4 h-4" />
                      Added to cart
                    </>
                  ) : isCardSampler && pickedCardIds.length !== 3 ? (
                    <>
                      <ShoppingBag className="w-4 h-4" />
                      Pick {3 - pickedCardIds.length} more
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-4 h-4" />
                      Add to cart
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setInquiryOpen(true)}
                    className="inline-flex items-center justify-center gap-3 px-8 py-5 text-sm uppercase tracking-[0.18em] font-medium bg-ink text-cream hover:bg-terracotta transition-colors"
                    data-testid="inquire-button"
                  >
                    Inquire about this piece
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-soft text-center leading-relaxed">
                    Payment plans available · Joseph responds personally within 1–2 days
                  </p>
                </>
              )}
              <Link
                to={`/${product.collection}`}
                className="text-center text-xs uppercase tracking-[0.18em] text-soft hover:text-terracotta transition-colors py-3"
                data-testid="back-to-collection"
              >
                ← Back to {collection?.name}
              </Link>
            </div>

            <div className="mt-12 pt-10 border-t border-ink/10 space-y-3 text-sm text-soft">
              <p>
                <span className="text-ink">Shipping —</span> Ships nationally
                from Colorado. Local pickup available at checkout.
              </p>
              <p>
                <span className="text-ink">Returns —</span> Each piece is
                inspected by the artist before shipping. Reach out if anything
                arrives less than perfect.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Book bundle upsell — shown on single books only */}
      {isSingleBook && bookBundle && (
        <section
          className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pb-24"
          data-testid="bundle-callout"
        >
          <Link
            to={`/products/${bookBundle.slug}`}
            className="group grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-8 items-center border border-ink/10 hover:border-ink transition-colors p-6 sm:p-8"
          >
            <div className="aspect-[4/5] bg-ink/[0.04] overflow-hidden">
              <img
                src={bookBundle.image}
                alt={bookBundle.title}
                className="w-full h-full object-contain p-4"
              />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-terracotta mb-3">
                Give both — save $3
              </p>
              <h3 className="font-display text-3xl lg:text-4xl text-ink tracking-tight leading-[1.05]">
                {bookBundle.title}
              </h3>
              <p className="mt-4 text-soft text-sm lg:text-base leading-relaxed max-w-xl">
                Both signed children&apos;s books together, packed and shipped from the studio.
                A ready-made gift for any young reader in your life.
              </p>
              <p className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink group-hover:text-terracotta transition-colors">
                ${bookBundle.price} — See the bundle
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </p>
            </div>
          </Link>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pb-32">
          <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-4">
            More from {collection?.name}
          </p>
          <h2 className="font-display text-3xl lg:text-4xl text-ink mb-14 tracking-tight">
            You might also like
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
            {related.map((p) => (
              <ProductTile key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      <SiteFooter />

      <InquiryDialog
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        product={product}
      />

      <WallPreview
        open={wallOpen}
        onClose={() => setWallOpen(false)}
        imageUrl={currentImage}
        title={product.title}
        sizeLabel={
          isOriginalPainting
            ? 'Original'
            : activePrintSize
            ? `${activePrintSize.label} print`
            : null
        }
      />

      {/* Custom-size inquiry for prints */}
      <InquiryDialog
        open={customSizeOpen}
        onClose={() => setCustomSizeOpen(false)}
        product={product}
        titleOverride={`Custom size — ${product.title}`}
        prefillMessage={`Hi Joseph — I'd love a larger ${product.title} print (e.g. 16×20 or 24×36). Could you let me know what's possible and the pricing?`}
      />

      {/* Book cover zoom lightbox — pinch, wheel, drag to pan */}
      {zoomOpen && (
        <div
          className="fixed inset-0 z-[70] bg-ink/95 flex items-center justify-center p-4 sm:p-8"
          onClick={() => setZoomOpen(false)}
          role="dialog"
          aria-label="Zoomed book spread"
          data-testid="book-zoom-modal"
        >
          <button
            type="button"
            onClick={() => setZoomOpen(false)}
            aria-label="Close zoom"
            className="absolute top-5 right-5 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-cream/10 hover:bg-cream/20 text-cream backdrop-blur-sm transition-colors"
            data-testid="book-zoom-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-full h-full max-w-6xl">
            <ZoomableImage src={currentImage} alt={product.title} />
          </div>
          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.18em] text-cream/60 pointer-events-none">
            Pinch or scroll to zoom · Drag to pan · Double-click to reset
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductPage;

// ---------------------------------------------------------------------------
// BookAwareImage — for books this renders a page-turn UI (chevrons, counter,
// crossfade, keyboard + swipe). For everything else it renders the standard
// image with badge.
// ---------------------------------------------------------------------------
const BookAwareImage = ({ product, imgIndex, setImgIndex, hasMultipleImages, currentImage, onZoom }) => {
  const isBook = product.collection === 'books';
  const isApparel = product.collection === 'apparel';
  const total = hasMultipleImages ? product.images.length : 1;
  const containerRef = useRef(null);
  const touchStartX = useRef(null);
  const [fadeKey, setFadeKey] = useState(0);

  const go = (delta) => {
    if (!hasMultipleImages) return;
    setImgIndex((idx) => {
      const next = (idx + delta + total) % total;
      return next;
    });
    setFadeKey((k) => k + 1);
  };

  // Keyboard arrow support — only when this book page is mounted
  useEffect(() => {
    if (!isBook || !hasMultipleImages) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isBook, hasMultipleImages, total]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const currentLabel = hasMultipleImages ? product.images[imgIndex]?.label : null;

  return (
    <div>
      <div
        ref={containerRef}
        onTouchStart={isBook ? onTouchStart : undefined}
        onTouchEnd={isBook ? onTouchEnd : undefined}
        className={cn(
          'relative overflow-hidden aspect-[4/5] group',
          isApparel ? 'bg-cream-deep' : 'bg-ink/[0.04]'
        )}
        data-testid={isBook ? 'book-viewer' : 'product-image'}
      >
        <img
          key={fadeKey}
          src={currentImage}
          alt={product.title}
          onClick={isBook && onZoom ? onZoom : undefined}
          className={cn(
            'w-full h-full',
            isApparel
              ? 'object-contain p-10 mix-blend-multiply'
              : isBook
              ? 'object-contain p-6 animate-book-fade cursor-zoom-in'
              : 'object-cover'
          )}
        />
        {product.badge && (
          <span className="absolute top-6 left-6 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] bg-cream/95 text-ink z-10">
            {product.badge}
          </span>
        )}
        {isBook && onZoom && (
          <button
            type="button"
            onClick={onZoom}
            aria-label="Zoom in"
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-cream/80 hover:bg-cream text-ink shadow-sm backdrop-blur-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
            data-testid="book-zoom"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        )}

        {/* Book-only page-turn overlay */}
        {isBook && hasMultipleImages && (
          <>
            <button
              type="button"
              aria-label="Previous page"
              onClick={() => go(-1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-cream/80 hover:bg-cream text-ink shadow-sm backdrop-blur-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              data-testid="book-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              aria-label="Next page"
              onClick={() => go(1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-cream/80 hover:bg-cream text-ink shadow-sm backdrop-blur-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              data-testid="book-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Bottom bar — label + counter */}
            <div className="absolute bottom-0 left-0 right-0 px-5 py-3 flex items-center justify-between bg-gradient-to-t from-ink/45 to-transparent text-cream text-xs">
              <span className="uppercase tracking-[0.18em]" data-testid="book-page-label">
                {currentLabel}
              </span>
              <span className="tabular-nums" data-testid="book-page-counter">
                {imgIndex + 1} / {total}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Book — page dot indicator instead of chunky tabs */}
      {isBook && hasMultipleImages && (
        <div
          className="mt-5 flex items-center justify-center gap-2.5"
          data-testid="book-dots"
        >
          {product.images.map((img, idx) => (
            <button
              key={img.id}
              type="button"
              onClick={() => {
                setImgIndex(idx);
                setFadeKey((k) => k + 1);
              }}
              aria-label={`Jump to ${img.label}`}
              className={cn(
                'h-1.5 rounded-full transition-all',
                imgIndex === idx ? 'bg-ink w-8' : 'bg-ink/25 w-4 hover:bg-ink/50'
              )}
              data-testid={`book-dot-${img.id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
